import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Container } from './Container';
import { Colors } from '../constants/colors';
import { Device } from '../constants/device';
import { USE_CASE_PATHS, UseCase, useCases } from '../config/useCasesConfig';
import { localizedPath } from '../helpers/localizedPath';
import { useUseCase } from '../helpers/useUseCase';

/** Id of the tabs strip; UseCaseBar takes over once it scrolls out of view. */
export const USE_CASE_PICKER_ID = 'use-case-picker';

/**
 * Lets the visitor say who they are, right below the hero: one tab per use case
 * and a panel pitching slp.blue to the picked one, so the switch shows next to
 * the click. The URL keeps the pick; the rest of the home page adapts to it too.
 */
export const UseCaseTabs = (): ReactElement => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const selected = useUseCase();
  const selectedIndex = useCases.indexOf(selected);

  // The panel animates only on a switch, not on the first render or hydration.
  const firstRender = useRef(true);
  const [switched, setSwitched] = useState(false);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setSwitched(true);
  }, [selected]);

  const pathOf = (useCase: UseCase) => localizedPath(pathname, USE_CASE_PATHS[useCase]);

  const pitch = `useCases.items.${selected}.pitch`;
  const tags = t(`${pitch}.tags`, { returnObjects: true }) as string[];
  const points = t(`${pitch}.points`, { returnObjects: true }) as string[];

  return (
    <Container>
      <Strip id={USE_CASE_PICKER_ID} aria-label={t('section.useCases')}>
        <Eyebrow>{t('section.useCases')}</Eyebrow>
        <Tabs>
          <Indicator aria-hidden style={{ transform: `translateX(${selectedIndex * 100}%)` }} />
          {useCases.map((useCase) => (
            <li key={useCase}>
              <Tab to={pathOf(useCase)} aria-current={useCase === selected ? 'page' : undefined} $selected={useCase === selected}>
                {t(`useCases.items.${useCase}.title`)}
              </Tab>
            </li>
          ))}
        </Tabs>
        <Panel key={selected} $animate={switched}>
          <div>
            <PanelTitle>{t(`${pitch}.title`)}</PanelTitle>
            <Lead>{t(`${pitch}.lead`)}</Lead>
            <Tags>
              {tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </Tags>
          </div>
          <Points>
            {points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </Points>
        </Panel>
      </Strip>
    </Container>
  );
};

const Strip = styled.nav`
  width: 100%;
  margin: 40px 0 60px;
  /* Room above the strip when the bar scrolls the visitor back up to it. */
  scroll-margin-top: 16px;
  padding: 26px 30px 30px;
  border-radius: 28px;
  background: ${Colors.background};

  ${Device.tablet} {
    margin: 30px 0 40px;
    padding: 20px 16px 24px;
    border-radius: 20px;
  }
`;

const Eyebrow = styled.div`
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${Colors.primaryDark};
`;

const Tabs = styled.ul`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${useCases.length}, 1fr);
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: 999px;
  background: ${Colors.white};
  box-shadow: inset 0 0 0 2px ${Colors.primaryLight};

  /* Narrower, the sliding oval gives way to tabs that colour themselves. */
  ${Device.laptop} {
    gap: 6px;
  }

  /* Phones get all four as a two by two grid, none hidden off screen. */
  ${Device.tablet} {
    grid-template-columns: 1fr 1fr;
    border-radius: 20px;
  }
`;

const Indicator = styled.li`
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 6px;
  width: calc((100% - 12px) / ${useCases.length});
  border-radius: 999px;
  background: ${Colors.primary};
  box-shadow: 0 6px 14px rgba(47, 85, 184, 0.35);
  transition: transform 0.35s cubic-bezier(0.3, 0.7, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${Device.laptop} {
    display: none;
  }
`;

const Tab = styled(Link)<{ $selected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 12px 10px;
  border-radius: 999px;
  color: ${({ $selected }) => ($selected ? Colors.white : Colors.primary)};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  text-decoration: none;
  transition: color 0.35s ease;

  &:hover {
    color: ${({ $selected }) => ($selected ? Colors.white : Colors.primaryDark)};
  }

  ${Device.laptop} {
    padding: 10px 12px;
    background: ${({ $selected }) => ($selected ? Colors.primary : 'transparent')};
    font-size: 15px;
  }

  ${Device.tablet} {
    min-height: 56px;
    border-radius: 14px;
    font-size: 14px;
  }
`;

const panelIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const Panel = styled.div<{ $animate: boolean }>`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 34px;
  align-items: center;
  margin-top: 26px;
  padding: 0 8px;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${panelIn} 0.35s ease both;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}

  ${Device.tablet} {
    grid-template-columns: 1fr;
    gap: 22px;
    padding: 0;
  }
`;

const PanelTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 28px;
  line-height: 1.25;
  color: ${Colors.primaryDark};

  ${Device.tablet} {
    font-size: 22px;
  }
`;

const Lead = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.55;
`;

const Tags = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;

  li {
    padding: 4px 12px;
    border-radius: 999px;
    border: 2px solid ${Colors.primaryLight};
    background: ${Colors.white};
    color: ${Colors.primary};
    font-size: 13px;
    font-weight: 700;
  }
`;

const Points = styled.ul`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 12px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.4;

    &::before {
      content: '✓';
      flex: none;
      display: grid;
      place-items: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: ${Colors.primary};
      color: ${Colors.white};
      font-size: 13px;
      font-weight: 700;
    }
  }
`;
